import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import UserCosts from './UserCosts';
import PageHeader from '../common/PageHeader/PageHeader';
import Table from '../common/Table/Table';
import Input from '../common/Input/Input';
import SearchBar from '../common/SearchBar/SearchBar';
import Button from '../common/Button/Button';
import './Metrics.css';

export default function Metrics() {
  const { apiClient } = useApi();
  const [activeTab, setActiveTab] = useState('summary');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchTerm, setSearchTerm] = useState('');
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pricingData, setPricingData] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSearch, setPricingSearch] = useState('');

  // Stats
  const [stats, setStats] = useState({
    tokens: 0,
    cost: 0,
    activeUsers: 0,
    models: 0
  });

  const loadMetricsData = useCallback(async () => {
    setLoading(true);

    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const fromDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const toDate = new Date(year, month, 0).toISOString().split('T')[0];
      const data = await apiClient.request(`/metrics/users/summary?from_date=${fromDate}&to_date=${toDate}`, {
        method: 'GET'
      });

      setUsersData(data || []);

      // Calculate totals
      setStats({
        tokens: data.reduce((sum, item) => sum + (item.total_tokens || 0), 0),
        cost: data.reduce((sum, item) => sum + (item.total_cost_usd || 0), 0),
        activeUsers: data.length,
        models: data.reduce((sum, item) => sum + (item.models_used || 0), 0)
      });

    } catch (err) {
      console.error('Error loading metrics:', err);
      setUsersData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, apiClient]);

  useEffect(() => {
    loadMetricsData();
  }, [loadMetricsData]);

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  const formatNumber = (val) => new Intl.NumberFormat('en-US').format(val);
  const formatModelPrice = (val) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(val || 0);

  useEffect(() => {
    const loadPricingData = async () => {
      setPricingLoading(true);
      try {
        const data = await apiClient.request('/pricing/', { method: 'GET' });
        setPricingData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading pricing data:', err);
        setPricingData([]);
      } finally {
        setPricingLoading(false);
      }
    };

    loadPricingData();
  }, [apiClient]);

  const filteredData = usersData.filter(item =>
    item.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPricingData = pricingData.filter((item) => {
    const term = pricingSearch.toLowerCase();
    return (
      item.provider?.toLowerCase().includes(term) ||
      item.model?.toLowerCase().includes(term)
    );
  });

  const pricingLastSyncText = useMemo(() => {
    if (!Array.isArray(pricingData) || pricingData.length === 0) return '';

    const maxEffectiveFrom = pricingData.reduce((acc, row) => {
      const ts = row?.effective_from ? new Date(row.effective_from).getTime() : NaN;
      return Number.isFinite(ts) ? Math.max(acc, ts) : acc;
    }, -Infinity);

    if (!Number.isFinite(maxEffectiveFrom) || maxEffectiveFrom === -Infinity) return '';
    return new Date(maxEffectiveFrom).toLocaleDateString();
  }, [pricingData]);

  const exportToCSV = () => {
    const exportData = {
      export_info: {
        generated_at: new Date().toISOString(),
        period: selectedMonth,
        total_users: filteredData.length,
        total_tokens: stats.tokens,
        total_cost: stats.cost,
        active_users: stats.activeUsers,
        models_used: stats.models
      },
      users: filteredData.map(user => ({
        user_id: user.user_id,
        email: user.email,
        display_name: user.display_name,
        total_tokens: user.total_tokens,
        total_cost_usd: user.total_cost_usd,
        models_used: user.models_used,
        requests: user.requests,
        percentage_of_total: user.percentage_of_total
      }))
    };

    const jsonData = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-summary-${selectedMonth}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Table Columns Definition
  const columns = [
    {
      header: 'User',
      accessor: 'display_name',
      width: '250px',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div className="user-avatar-small">
            {row.avatar_url ? <img src={row.avatar_url} alt="" /> : row.display_name?.charAt(0)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{row.display_name}</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.email}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Total Tokens',
      accessor: 'total_tokens',
      width: '150px',
      render: (row) => formatNumber(row.total_tokens)
    },
    {
      header: 'Total Cost',
      accessor: 'total_cost_usd',
      width: '120px',
      render: (row) => <span style={{ color: 'var(--success)', fontWeight: 600 }}>{formatCurrency(row.total_cost_usd)}</span>
    },
    {
      header: 'Models',
      accessor: 'models_used',
      width: '100px'
    },
    {
      header: 'Requests',
      accessor: 'requests',
      width: '120px',
      render: (row) => formatNumber(row.requests)
    },
    {
      header: '% of Total',
      accessor: 'percentage_of_total',
      width: '120px',
      render: (row) => `${row.percentage_of_total || 0}%`
    }
  ];

  const pricingColumns = [
    {
      header: 'Provider',
      accessor: 'provider',
      width: '140px',
      render: (row) => (
        <span className="provider-badge">{(row.provider || '').toUpperCase()}</span>
      )
    },
    {
      header: 'Model',
      accessor: 'model',
      width: '260px',
      render: (row) => <span style={{ fontWeight: 600 }}>{row.model}</span>
    },
    {
      header: 'Input (per 1M)',
      accessor: 'price_per_1k_input',
      width: '160px',
      render: (row) => formatModelPrice((row.price_per_1k_input || 0) * 1000)
    },
    {
      header: 'Output (per 1M)',
      accessor: 'price_per_1k_output',
      width: '170px',
      render: (row) => formatModelPrice((row.price_per_1k_output || 0) * 1000)
    }
  ];


  return (
    <div className="page-container metrics-view">
      <div className="page-content">
        <PageHeader
          title="Usage Metrics"
          description="Monitor token consumption, costs, and usage patterns."
          icon="fas fa-chart-line"
          accentColor="blue"
        />

        {/* Tabs */}
        <div className="metrics-tabs">
          <button
            className={`metrics-tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            <i className="fas fa-chart-bar"></i> Usage Summary
          </button>
          <button
            className={`metrics-tab ${activeTab === 'user-costs' ? 'active' : ''}`}
            onClick={() => setActiveTab('user-costs')}
          >
            <i className="fas fa-users"></i> User Costs
          </button>
          <button
            className={`metrics-tab ${activeTab === 'pricing' ? 'active' : ''}`}
            onClick={() => setActiveTab('pricing')}
          >
            <i className="fas fa-tags"></i> LLM Pricing
          </button>
        </div>

        {activeTab === 'summary' ? (
          <>
            {/* Summary Cards */}
            <div className="metrics-summary">
              <div className="summary-card">
                <div className="summary-icon"><i className="fas fa-coins"></i></div>
                <div className="summary-content">
                  <h3>{formatNumber(stats.tokens)}</h3>
                  <p>Total Tokens</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon" style={{ color: 'var(--success)', background: 'rgba(16, 185, 129, 0.1)' }}>
                  <i className="fas fa-dollar-sign"></i>
                </div>
                <div className="summary-content">
                  <h3>{formatCurrency(stats.cost)}</h3>
                  <p>Total Cost</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon" style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.1)' }}>
                  <i className="fas fa-users"></i>
                </div>
                <div className="summary-content">
                  <h3>{stats.activeUsers}</h3>
                  <p>Active Users</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon" style={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)' }}>
                  <i className="fas fa-microchip"></i>
                </div>
                <div className="summary-content">
                  <h3>{stats.models}</h3>
                  <p>Models Used</p>
                </div>
              </div>
            </div>

            {/* Toolbar */}
            <div className="metrics-toolbar">
              <div style={{ width: '200px' }}>
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                />
              </div>
              <div style={{ width: '300px' }}>
                <SearchBar
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="toolbar-spacer"></div>
              <Button variant="secondary" icon="fas fa-download" onClick={exportToCSV}>
                Export CSV
              </Button>
            </div>

            {/* Main Table */}
            <Table
              columns={columns}
              data={filteredData}
              isLoading={loading}
              emptyMessage="No usage data found for this period."
              emptyIcon="fas fa-chart-line"
            />
          </>
        ) : activeTab === 'user-costs' ? (
          <UserCosts />
        ) : (
          <>
            <div className="metrics-toolbar">
              <div style={{ width: '320px' }}>
                <SearchBar
                  placeholder="Search provider or model..."
                  value={pricingSearch}
                  onChange={(e) => setPricingSearch(e.target.value)}
                />
              </div>
              <div className="toolbar-spacer"></div>
              {pricingLastSyncText && (
                <div className="pricing-last-sync">
                  Last sync: {pricingLastSyncText}
                </div>
              )}
            </div>

            <Table
              columns={pricingColumns}
              data={filteredPricingData}
              isLoading={pricingLoading}
              emptyMessage="No pricing data available."
            />
          </>
        )}
      </div>
    </div>
  );
}
